import * as RadixDialog from "@radix-ui/react-dialog";
import type { ReactNode, RefObject } from "react";

import { OverlayContentContext, focusReturnElement, useOverlayGrant, useReturnFocusOnOpen } from "@/lib/overlay-gate";

/**
 * `DESIGN.md` § Components `dialog`: centred, `{rounded.lg}`, `{spacing.6}`
 * padding, one scrim, one shadow, 480px max width. Radix `Dialog` supplies
 * the focus trap, focus return, and `aria` wiring (VC-2.6-04) — this file
 * only styles it and wires the app-specific rules Radix leaves to the
 * caller: the shared "no overlay opens on top of another" gate
 * (`lib/overlay-gate.ts`) and the confirm-dialog-closes-only-by-its-buttons
 * rule (`dismissible`, VC-2.6-02).
 *
 * Desktop-only styling — `ConfirmDialog.tsx` (and any future caller)
 * chooses between this and `Sheet.tsx` by viewport; this component does not
 * itself branch on one, keeping "dialog on desktop, sheet on phone" as one
 * decision made in exactly one place.
 *
 * `max-h-[85vh] overflow-y-auto` (gap-fill, found verifying Story 12.1's own
 * equipment capture form live on a real phone viewport): every caller
 * before that story's own `AddEquipmentDialog` was short enough that this
 * never mattered — with no height cap, `top-1/2 -translate-y-1/2` centers
 * content taller than the viewport by pushing it equally off-screen both
 * above and below, with the parent's own `overflow: hidden` (Radix's own
 * scroll lock) leaving no way to reach either end. A pre-existing gap in
 * this shared primitive, not specific to any one caller's own content.
 */
export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  /**
   * Non-destructive overlays close via `Esc` and the scrim (the default,
   * matching every overlay before a confirm dialog exists). A confirm
   * dialog passes `false`: `Esc`, the scrim, and any outside interaction
   * are all refused, and the dialog closes only through its own Cancel or
   * confirm button calling `onOpenChange` directly.
   */
  dismissible?: boolean;
  /**
   * Where to send focus on close when the element that opened this dialog
   * is no longer in the document — the confirm path, where the trigger's
   * own row/chip unmounts as part of the same action that closed the
   * dialog (`lib/overlay-gate.ts`'s `focusReturnElement` doc explains the
   * live-found bug this covers). A stable, always-present element the
   * caller owns — a page heading, a persistent container — not this
   * component's concern to invent.
   */
  fallbackFocusRef?: RefObject<HTMLElement | null>;
}

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  dismissible = true,
  fallbackFocusRef,
}: DialogProps) {
  const granted = useOverlayGrant(open);
  const returnFocusTo = useReturnFocusOnOpen(open);

  function handleOpenChange(next: boolean) {
    if (!next && !dismissible) {
      return;
    }
    onOpenChange(next);
  }

  return (
    <RadixDialog.Root open={granted} onOpenChange={handleOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-50 bg-scrim data-[state=closed]:animate-overlay-out data-[state=open]:animate-overlay-in" />
        <RadixDialog.Content
          onEscapeKeyDown={(event) => {
            if (!dismissible) {
              event.preventDefault();
            }
          }}
          onPointerDownOutside={(event) => {
            if (!dismissible) {
              event.preventDefault();
            }
          }}
          onInteractOutside={(event) => {
            if (!dismissible) {
              event.preventDefault();
            }
          }}
          onCloseAutoFocus={(event) => {
            // Radix's own default focus-return only works for a Radix
            // `Dialog.Trigger`, which this component never renders (its
            // trigger is an arbitrary caller-owned button) — see
            // `useReturnFocusOnOpen`'s own doc for why, and how this was
            // found (live verification, not inspection). `focusReturnElement`
            // additionally falls back past a trigger that unmounted as part
            // of the same close action (the confirm path) to a caller-stable
            // element, if one was supplied.
            const target = focusReturnElement(returnFocusTo, fallbackFocusRef);
            if (target) {
              event.preventDefault();
              target.focus();
            }
          }}
          {...(description === undefined ? { "aria-describedby": undefined } : {})}
          className="fixed left-1/2 top-1/2 z-50 max-h-[85vh] w-[90%] max-w-dialog-max -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg bg-surface p-6 shadow-overlay data-[state=closed]:animate-dialog-out data-[state=open]:animate-dialog-in"
        >
          <RadixDialog.Title className="text-title font-semibold text-ink">{title}</RadixDialog.Title>
          {description !== undefined ? (
            <RadixDialog.Description className="mt-2 text-body text-ink-secondary">
              {description}
            </RadixDialog.Description>
          ) : null}
          {/* `OverlayContentContext.ts`'s own doc: a `Popover` (e.g. a
           * `Combobox`'s listbox) rendered anywhere inside this dialog's own
           * content is a visual child of this one already-granted overlay,
           * never a second one competing for the same gate slot. */}
          <OverlayContentContext.Provider value={true}>{children}</OverlayContentContext.Provider>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
